import { useState } from 'react';

import { isRateLimited, reasonFrom } from '../api/reason';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';
import { Button, Field, Input, LINK_CLASS, LINK_QUIET_CLASS, Notice, Check } from '@efira/ui';
import { fieldErrors, signInSchema } from '@efira/contracts';

import { signIn } from '../auth/client';
import { AuthLayout } from './AuthLayout';
import { SocialButtons } from './SocialButtons';

/**
 * Вход.
 *
 * Сообщение об ошибке ЕДИНОЕ для неверной пары: «почта или пароль не подходят».
 * Раздельные сообщения («такого адреса нет» против «неверный пароль») - это тот
 * же способ перебрать зарегистрированные почты, что и разные коды ответа на
 * регистрации.
 *
 * Исключение - неподтверждённая почта: тут пользователь уже доказал, что знает
 * пару, и молчать значило бы оставить его без объяснения, почему не пускает.
 */
export function SignInScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  // Куда пользователь шёл до того, как его развернуло на вход.
  const state = location.state as { from?: string; passwordChanged?: boolean } | null;
  const from = state?.from ?? '/app';
  // После смены пароля пользователь приходит сюда - и должен понимать, что
  // смена удалась, иначе экран входа читается как «что-то пошло не так».

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);
    setNeedsVerification(false);

    const parsed = signInSchema.safeParse({ email, password, rememberMe });

    if (!parsed.success) {
      setErrors(fieldErrors(parsed.error));
      return;
    }

    setErrors({});
    setLoading(true);

    const { error } = await signIn.email({
      email: parsed.data.email,
      password: parsed.data.password,
      rememberMe: parsed.data.rememberMe,
    });

    setLoading(false);

    if (error !== null && error !== undefined) {
      if (error.code === 'EMAIL_NOT_VERIFIED' || error.status === 403) {
        setNeedsVerification(true);
        return;
      }

      // «Почта или пароль не подходят» - ОСОЗНАННО общий текст: сервер не
      // различает незнакомый адрес и неверный пароль, иначе по ответу можно
      // перебирать зарегистрированные почты. Но он общий только для отказа
      // по учётным данным: причина, присланная сервером - отказ Turnstile,
      // блокировка, сбой, - показывается как есть.
      /* Три РАЗНЫЕ причины - три разных текста: неверная пара,
         неподтверждённая почта (выше) и перебор попыток. Одна общая строка
         на три случая заставляет человека гадать, что именно не так.
         Время до следующей попытки приходит от сервера - он один его знает. */
      setFormError(
        isRateLimited(error)
          ? reasonFrom(error, 'Слишком много попыток подряд. Попробуйте через несколько минут.')
          : reasonFrom(error, 'Почта или пароль не подходят.'),
      );
      return;
    }

    navigate(from, { replace: true });
  }

  return (
    <AuthLayout
      title="Вход"
      /* Куда именно человек входит - одной строкой: заголовок из одного
         слова не отвечает на это ни новому пользователю, ни вернувшемуся
         через месяц. */
      subtitle="Консоль Efira Live: игры, оверлей и настройки эфира."
      footer={
        <>
          Нет аккаунта?{' '}
          <RouterLink data-auth="signup" className={LINK_CLASS} to="/signup">
            Зарегистрироваться
          </RouterLink>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError !== null ? (
          <div>
            <Notice tone="error" title="Не получилось войти" live>
              {formError}
            </Notice>
          </div>
        ) : null}

        {state?.passwordChanged === true ? (
          <div>
            <Notice tone="success" title="Пароль изменён">
              Войдите с новым паролем.
            </Notice>
          </div>
        ) : null}

        {needsVerification ? (
          <div>
            <Notice tone="warning" title="Почта не подтверждена" live>
              Мы отправляли письмо со ссылкой при регистрации. Откройте его — и сразу сможете войти.{' '}
              <RouterLink className={LINK_CLASS} to="/check-email" state={{ email }}>
                Отправить письмо ещё раз
              </RouterLink>
            </Notice>
          </div>
        ) : null}

        {/* Обязательны оба поля - значит метка обязательности не отмечает
            ничего: `requiredMark={false}` снимает её, оставив саму
            обязательность. */}
        <Field label="Почта" error={errors['email']} required requiredMark={false}>
          {(props) => (
            <Input
              {...props}
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              data-auth="email"
              autoComplete="username"
              inputMode="email"
              autoFocus
              placeholder="you@example.com"
            />
          )}
        </Field>

        <Field label="Пароль" error={errors['password']} required requiredMark={false}>
          {(props) => (
            <Input
              {...props}
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
              data-auth="password"
              autoComplete="current-password"
            />
          )}
        </Field>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <span data-auth="remember">
            <Check checked={rememberMe} onChange={setRememberMe} label="Запомнить меня" />
          </span>
          <RouterLink data-auth="forgot" className={LINK_QUIET_CLASS} to="/forgot-password">
            Забыли пароль?
          </RouterLink>
        </div>

        <div className="mt-2 flex flex-col gap-3">
          <Button data-auth="submit" type="submit" variant="primary" block loading={loading}>
            Войти
          </Button>

          <SocialButtons />
        </div>
      </form>
    </AuthLayout>
  );
}
